import { useEffect } from "react";

type SeoProps = {
  title: string;
  description?: string;
  canonicalPath?: string;
  imageUrl?: string;
  ogType?: string;
  keywords?: string;
};

function originForSeo(): string {
  const envOrigin = typeof import.meta.env.VITE_PUBLIC_SITE_ORIGIN === "string"
    ? import.meta.env.VITE_PUBLIC_SITE_ORIGIN.trim()
    : "";
  const raw = envOrigin || (typeof window !== "undefined" ? window.location.origin : "https://www.deskreplay.com");
  return raw.replace(/\/$/, "");
}

function upsertMetaByName(name: string): HTMLMetaElement {
  let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = name;
    document.head.appendChild(meta);
  }
  return meta;
}

function upsertMetaByProperty(property: string): HTMLMetaElement {
  let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("property", property);
    document.head.appendChild(meta);
  }
  return meta;
}

function upsertCanonicalLink(): HTMLLinkElement {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.rel = "canonical";
    document.head.appendChild(link);
  }
  return link;
}

export function Seo({ title, description, canonicalPath, imageUrl, ogType, keywords }: SeoProps) {
  useEffect(() => {
    const base = "DeskReplay";
    document.title = title.includes("DeskReplay") ? title : `${title} · ${base}`;

    const origin = originForSeo();
    const path = canonicalPath ?? (typeof window !== "undefined" ? window.location.pathname : "/");
    const canonicalUrl = `${origin}${path === "/" ? "/" : path}`;

    if (description) {
      upsertMetaByName("description").content = description;
    }

    upsertCanonicalLink().href = canonicalUrl;

    const ogImage = imageUrl ?? `${origin}/preview.jpg`;
    const ogTitle = document.title;
    const ogDesc = description ?? "";

    upsertMetaByProperty("og:site_name").content = base;
    upsertMetaByProperty("og:title").content = ogTitle;
    if (ogDesc) upsertMetaByProperty("og:description").content = ogDesc;
    upsertMetaByProperty("og:image").content = ogImage;
    upsertMetaByProperty("og:image:width").content = "1200";
    upsertMetaByProperty("og:image:height").content = "630";
    upsertMetaByProperty("og:image:alt").content = ogTitle;
    upsertMetaByProperty("og:url").content = canonicalUrl;
    upsertMetaByProperty("og:type").content = ogType ?? "website";
    upsertMetaByProperty("og:locale").content = "en_US";

    upsertMetaByName("twitter:card").content = "summary_large_image";
    upsertMetaByName("twitter:site").content = "@DeskReplay";
    upsertMetaByName("twitter:title").content = ogTitle;
    if (ogDesc) upsertMetaByName("twitter:description").content = ogDesc;
    upsertMetaByName("twitter:image").content = ogImage;

    if (keywords) {
      upsertMetaByName("keywords").content = keywords;
    }
  }, [title, description, canonicalPath, imageUrl, ogType, keywords]);

  return null;
}
